/**
 * Notifications Hooks - React Query Adapter
 *
 * Phase 3.1: Single source of truth for notification state.
 * React Query IS the cache - no bespoke state.
 *
 * Features:
 * - useUnreadNotificationCount(): Query for unread count
 * - useMarkNotificationRead(): Mutation to mark single as read
 * - useMarkAllNotificationsRead(): Mutation to mark all as read
 *
 * Note: BroadcastChannel for cross-tab sync is handled by NotificationContext
 * which acts as a thin adapter calling setQueryData.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';

// ============================================================================
// Types (exported for use in components)
// ============================================================================

export interface NotificationUser {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface Notification {
  id: string;
  type: 'new_follower' | 'recommendation' | 'milestone' | 'list_shared';
  body: string;
  data: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  from_user: NotificationUser | null;
}

interface NotificationsListResponse {
  notifications: Notification[];
  next_cursor: string | null;
  has_more: boolean;
}

// ============================================================================
// useUnreadNotificationCount - Query hook for unread count
// ============================================================================

interface UseUnreadNotificationCountResult {
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to get the unread notification count.
 * Single source of truth - NotificationContext reads from this cache.
 */
export function useUnreadNotificationCount(): UseUnreadNotificationCountResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useQuery({
    queryKey: queryKeys.notifications.unreadCount(userId || ''),
    queryFn: async () => {
      if (!token) return 0;

      const data = await apiFetch<{ unread_count?: number }>('/notifications/unread-count', {
        token,
      });
      return data.unread_count || 0;
    },
    // Only fetch if user is logged in
    enabled: !!userId && !!token,
    // Keep data fresh for 60 seconds (no polling per requirements)
    staleTime: 60_000,
  });

  return {
    unreadCount: query.data ?? 0,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

// ============================================================================
// useMarkNotificationRead - Mutation hook for marking single as read
// ============================================================================

interface MarkReadVariables {
  notificationId: string;
}

interface UseMarkNotificationReadResult {
  markAsRead: (notificationId: string) => Promise<boolean>;
  isLoading: boolean;
}

/**
 * Hook to mark a single notification as read.
 * Uses optimistic updates with rollback on error.
 * Updates both unread count and notification list caches.
 */
export function useMarkNotificationRead(): UseMarkNotificationReadResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (variables: MarkReadVariables) => {
      if (!token) throw new Error('Not authenticated');

      const data = await apiFetch<{ success?: boolean; already_read?: boolean }>(
        `/notifications/${variables.notificationId}/read`,
        {
          token,
          method: 'PATCH',
        }
      );
      return data;
    },

    onMutate: async (variables) => {
      if (!userId) return;

      const countKey = queryKeys.notifications.unreadCount(userId);
      const listBaseKey = queryKeys.notifications.listBase(userId);

      // Cancel any outgoing refetches for count and lists
      await queryClient.cancelQueries({ queryKey: countKey });
      await queryClient.cancelQueries({ queryKey: listBaseKey });

      // Snapshot previous count
      const previousCount = queryClient.getQueryData<number>(countKey);

      // Snapshot and update all list caches
      const listCacheSnapshots: Array<{ queryKey: readonly unknown[]; data: unknown }> = [];
      queryClient.getQueriesData<NotificationsListResponse | { pages: NotificationsListResponse[] }>({ queryKey: listBaseKey }).forEach(([key, data]) => {
        if (data) {
          listCacheSnapshots.push({ queryKey: key, data });
          // Update is_read in list cache
          if ('pages' in data) {
            // Infinite query
            queryClient.setQueryData<{ pages: NotificationsListResponse[]; pageParams: unknown[] }>(key, (old) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  notifications: page.notifications.map((n) =>
                    n.id === variables.notificationId ? { ...n, is_read: true } : n
                  ),
                })),
              };
            });
          } else {
            // Simple query
            queryClient.setQueryData<NotificationsListResponse>(key, (old) => {
              if (!old) return old;
              return {
                ...old,
                notifications: old.notifications.map((n) =>
                  n.id === variables.notificationId ? { ...n, is_read: true } : n
                ),
              };
            });
          }
        }
      });

      // Optimistically decrement count
      queryClient.setQueryData<number>(countKey, (old) => Math.max(0, (old ?? 0) - 1));

      // Return context for rollback
      return { previousCount, countKey, listCacheSnapshots };
    },

    onError: (_error, _variables, context) => {
      // Rollback count
      if (context?.countKey && context.previousCount !== undefined) {
        queryClient.setQueryData(context.countKey, context.previousCount);
      }
      // Rollback list caches
      context?.listCacheSnapshots?.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
    },

    onSuccess: (data, _variables, context) => {
      // If it was already read, restore the count (no actual decrement)
      if (data.already_read && context?.countKey && context.previousCount !== undefined) {
        queryClient.setQueryData(context.countKey, context.previousCount);
      }
    },
  });

  const markAsRead = async (notificationId: string): Promise<boolean> => {
    if (!userId || !token) return false;

    try {
      await mutation.mutateAsync({ notificationId });
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  };

  return {
    markAsRead,
    isLoading: mutation.isPending,
  };
}

// ============================================================================
// useMarkAllNotificationsRead - Mutation hook for marking all as read
// ============================================================================

interface UseMarkAllNotificationsReadResult {
  markAllAsRead: () => Promise<boolean>;
  isLoading: boolean;
}

/**
 * Hook to mark all notifications as read.
 * Uses optimistic updates with rollback on error.
 * Updates both unread count and notification list caches.
 */
export function useMarkAllNotificationsRead(): UseMarkAllNotificationsReadResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch('/notifications/mark-all-read', {
        token,
        method: 'POST',
      });
      return true;
    },

    onMutate: async () => {
      if (!userId) return;

      const countKey = queryKeys.notifications.unreadCount(userId);
      const listBaseKey = queryKeys.notifications.listBase(userId);

      // Cancel any outgoing refetches for count and lists
      await queryClient.cancelQueries({ queryKey: countKey });
      await queryClient.cancelQueries({ queryKey: listBaseKey });

      // Snapshot previous count
      const previousCount = queryClient.getQueryData<number>(countKey);

      // Snapshot and update all list caches to mark all as read
      const listCacheSnapshots: Array<{ queryKey: readonly unknown[]; data: unknown }> = [];
      queryClient.getQueriesData<NotificationsListResponse | { pages: NotificationsListResponse[] }>({ queryKey: listBaseKey }).forEach(([key, data]) => {
        if (data) {
          listCacheSnapshots.push({ queryKey: key, data });
          // Mark all as read in list cache
          if ('pages' in data) {
            // Infinite query
            queryClient.setQueryData<{ pages: NotificationsListResponse[]; pageParams: unknown[] }>(key, (old) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  notifications: page.notifications.map((n) => ({ ...n, is_read: true })),
                })),
              };
            });
          } else {
            // Simple query
            queryClient.setQueryData<NotificationsListResponse>(key, (old) => {
              if (!old) return old;
              return {
                ...old,
                notifications: old.notifications.map((n) => ({ ...n, is_read: true })),
              };
            });
          }
        }
      });

      // Optimistically set count to 0
      queryClient.setQueryData<number>(countKey, 0);

      // Return context for rollback
      return { previousCount, countKey, listCacheSnapshots };
    },

    onError: (_error, _variables, context) => {
      // Rollback count
      if (context?.countKey && context.previousCount !== undefined) {
        queryClient.setQueryData(context.countKey, context.previousCount);
      }
      // Rollback list caches
      context?.listCacheSnapshots?.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
    },

    // FIX 4: Invalidate on success to prevent stale refetch overwriting optimistic cache
    onSuccess: (_data, _variables, context) => {
      if (!userId) return;
      // Invalidate to sync with server state and prevent stale data
      queryClient.invalidateQueries({ queryKey: context?.countKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.listBase(userId) });
    },
  });

  const markAllAsRead = async (): Promise<boolean> => {
    if (!userId || !token) return false;

    try {
      await mutation.mutateAsync();
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  };

  return {
    markAllAsRead,
    isLoading: mutation.isPending,
  };
}

// ============================================================================
// useNotificationsList - Query hook for dropdown list (simple, non-paginated)
// ============================================================================

interface UseNotificationsListOptions {
  limit?: number;
}

interface UseNotificationsListResult {
  notifications: Notification[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to get a simple list of notifications for dropdown.
 * Uses React Query as single source of truth.
 */
export function useNotificationsList(options: UseNotificationsListOptions = {}): UseNotificationsListResult {
  const { limit = 10 } = options;
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useQuery({
    queryKey: queryKeys.notifications.list(userId || '', { limit, cursor: null, type: null }),
    queryFn: async () => {
      if (!token) return { notifications: [], next_cursor: null, has_more: false };

      const data = await apiFetch<NotificationsListResponse>(`/notifications?limit=${limit}`, {
        token,
      });
      return data;
    },
    enabled: !!userId && !!token,
    staleTime: 30_000, // 30 seconds - dropdown data can be slightly stale
  });

  return {
    notifications: query.data?.notifications ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => query.refetch(),
  };
}

// ============================================================================
// useNotificationsInfinite - Infinite query for paginated notifications page
// ============================================================================

interface UseNotificationsInfiniteOptions {
  limit?: number;
  type?: string | null;
}

interface UseNotificationsInfiniteResult {
  notifications: Notification[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  fetchNextPage: () => void;
  refetch: () => void;
}

/**
 * Hook for infinite scrolling notifications list (NotificationsPage).
 * Flattens pages into single list for easy rendering.
 */
export function useNotificationsInfinite(options: UseNotificationsInfiniteOptions = {}): UseNotificationsInfiniteResult {
  const { limit = 20, type = null } = options;
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useInfiniteQuery({
    queryKey: queryKeys.notifications.list(userId || '', { limit, cursor: null, type }),
    queryFn: async ({ pageParam }) => {
      if (!token) return { notifications: [], next_cursor: null, has_more: false };

      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam);
      if (type) params.set('type', type);

      const data = await apiFetch<NotificationsListResponse>(`/notifications?${params}`, {
        token,
      });
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.next_cursor : undefined),
    enabled: !!userId && !!token,
    staleTime: 30_000,
  });

  // Flatten all pages into a single notifications array
  const notifications = query.data?.pages.flatMap((page) => page.notifications) ?? [];

  return {
    notifications,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    error: query.error as Error | null,
    fetchNextPage: () => query.fetchNextPage(),
    refetch: () => query.refetch(),
  };
}
